import {
  Ban,
  Bike,
  CheckCircle2,
  ChefHat,
  PackageCheck,
  Truck,
  XCircle,
} from 'lucide-react';

// Single source of truth for how the customer app reads an order's fulfilment
// state. Extracted from OrderDetailPage so the list and the detail screen can
// never drift into two different interpretations of the same backend data.
//
// Keys are the values the backend records in fulfillment.statusHistory (the
// fulfillment.status enum in models/Sale.js, plus `assigned`, which the
// customer-app projection derives from driverAssignment, and the packing
// aliases it recognises).
export const STATUS_META = {
  placed: { label: 'Order Placed', icon: CheckCircle2 },
  confirmed: { label: 'Confirmed', icon: CheckCircle2 },
  preparing: { label: 'Packed', icon: ChefHat },
  packed: { label: 'Packed', icon: ChefHat },
  allocated: { label: 'Packed', icon: ChefHat },
  partially_allocated: { label: 'Partially Packed', icon: ChefHat },
  assigned: { label: 'Assigned', icon: Bike },
  out_for_delivery: { label: 'Out for Delivery', icon: Truck },
  delivered: { label: 'Delivered', icon: PackageCheck },
  failed: { label: 'Failed', icon: XCircle },
  cancelled: { label: 'Cancelled', icon: Ban },
};

// "out_for_delivery" -> "Out For Delivery". Fallback for a status the backend
// starts emitting before this map learns about it.
export const titleCaseStatus = (status) =>
  String(status)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

/** The order's effective fulfilment status, matching OrderDetailPage. */
export const resolveOrderStatus = (order) =>
  order?.fulfillment?.status || order?.fulfillmentStatus || order?.status || 'placed';

/** Terminal states an order cannot progress out of. */
export const TERMINAL_FAILURE_STATUSES = ['failed', 'cancelled'];

export const isTerminalFailure = (status) => TERMINAL_FAILURE_STATUSES.includes(status);

export const statusLabel = (status) => STATUS_META[status]?.label || titleCaseStatus(status);

// Badge tones. Terminal failures read red; everything else uses the brand's
// progress palette, with the in-progress stages amber so a shopper can tell
// "still being prepared" from "on the way" at a glance.
export const statusTone = (status) => {
  if (isTerminalFailure(status)) return 'text-red-700 bg-red-50 border-red-100';
  if (status === 'delivered') return 'text-success-700 bg-success-50 border-success-100';
  if (['preparing', 'packed', 'allocated', 'partially_allocated'].includes(status)) {
    return 'text-highlight-600 bg-highlight-50 border-highlight-100';
  }
  if (['assigned', 'out_for_delivery'].includes(status)) {
    return 'text-emerald-800 bg-emerald-50 border-emerald-100';
  }
  return 'text-emerald-700 bg-emerald-50 border-emerald-100';
};

// The lifecycle the tracker draws. Each stage lists the raw backend statuses
// that satisfy it, so the packing aliases and the derived `assigned` state all
// land on the right step.
export const PROGRESS_STAGES = [
  { id: 'placed', label: 'Placed', matches: ['placed'], icon: STATUS_META.placed.icon },
  { id: 'confirmed', label: 'Confirmed', matches: ['confirmed'], icon: STATUS_META.confirmed.icon },
  {
    id: 'preparing',
    label: 'Preparing',
    matches: ['preparing', 'packed', 'allocated', 'partially_allocated'],
    icon: STATUS_META.preparing.icon,
  },
  {
    id: 'out_for_delivery',
    label: 'Out for Delivery',
    matches: ['assigned', 'out_for_delivery'],
    icon: STATUS_META.out_for_delivery.icon,
  },
  { id: 'delivered', label: 'Delivered', matches: ['delivered'], icon: STATUS_META.delivered.icon },
];

const stageIndexFor = (status) =>
  PROGRESS_STAGES.findIndex((stage) => stage.matches.includes(status));

/**
 * Builds the tracker for one order, entirely from its own data.
 *
 * - `at` is only ever a real timestamp from fulfillment.statusHistory; a stage
 *   the order has not actually recorded carries no time.
 * - A terminal failure returns `failed: true` and no future stages, so a
 *   cancelled order can never be drawn as if it were on its way.
 */
export const buildOrderProgress = (order) => {
  const status = resolveOrderStatus(order);
  const history = Array.isArray(order?.fulfillment?.statusHistory)
    ? order.fulfillment.statusHistory
    : [];

  // Earliest recorded time per raw status.
  const timeByStatus = new Map();
  history.forEach((entry) => {
    const raw = typeof entry?.status === 'string' ? entry.status.trim() : '';
    if (!raw || !entry?.at || timeByStatus.has(raw)) return;
    timeByStatus.set(raw, entry.at);
  });

  const reachedIndex = history.reduce((highest, entry) => {
    const index = stageIndexFor(entry?.status);
    return index > highest ? index : highest;
  }, stageIndexFor(status));

  if (isTerminalFailure(status)) {
    const failedAt = timeByStatus.get(status) || null;
    // Only the stages the order genuinely reached before it stopped.
    const completed = PROGRESS_STAGES.slice(0, Math.max(reachedIndex + 1, 0)).map((stage) => ({
      ...stage,
      state: 'complete',
      at: stage.matches.map((raw) => timeByStatus.get(raw)).find(Boolean) || null,
    }));
    return {
      failed: true,
      status,
      label: statusLabel(status),
      at: failedAt,
      steps: completed,
    };
  }

  const currentIndex = Math.max(stageIndexFor(status), 0);
  const steps = PROGRESS_STAGES.map((stage, index) => ({
    ...stage,
    state: index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming',
    at: stage.matches.map((raw) => timeByStatus.get(raw)).find(Boolean) || null,
  }));

  return { failed: false, status, label: statusLabel(status), at: null, steps };
};

/** Live delivery tracking is only meaningful once the order is on its way. */
export const supportsLiveTracking = (order) =>
  ['assigned', 'out_for_delivery'].includes(resolveOrderStatus(order));
